import { useRef } from 'react';
import { Alert, Form, Input, Modal, type InputRef } from 'antd';
import { useNavigate } from 'react-router-dom';
import { runUnsavedGuards } from '../hooks/useUnsavedChangesGuard';
import { normalizeServerInput, validateServerInput } from '../lib/createServer';
import { routes } from '../lib/routes';
import { useServerStore } from '../stores/useServerStore';

interface Props {
  onClose: () => void;
  restoreFocus: () => void;
}

interface CreateServerFields {
  name: string;
  icon: string;
}

export default function CreateServerModal({ onClose, restoreFocus }: Props) {
  const navigate = useNavigate();
  const [form] = Form.useForm<CreateServerFields>();
  const nameRef = useRef<InputRef>(null);
  const submitLatch = useRef(false);
  const busy = useServerStore((state) => state.createBusy);
  const serverError = useServerStore((state) => state.createError);
  const createRecovery = useServerStore((state) => state.createRecovery);
  const createServer = useServerStore((state) => state.createServer);
  const setCreateError = useServerStore((state) => state.setCreateError);
  const fieldsLocked = busy || createRecovery !== null;

  const close = () => {
    if (busy) return;
    onClose();
    setTimeout(restoreFocus, 0);
  };

  const executeCreate = async (name?: string, icon?: string) => {
    if (submitLatch.current || busy || !runUnsavedGuards()) return;
    submitLatch.current = true;
    try {
      const receipt = await createServer(name, icon);
      onClose();
      // Enter through the Project route so AppShell owns the whole transition:
      // it stages channels/members, commits the new Project context, and only
      // then publishes the landing channel route. Navigating to the channel
      // here would expose a target channel id before its Project snapshot exists.
      navigate(routes.server(receipt.guildId));
    } catch {
      submitLatch.current = false;
    }
  };

  const submit = (values: CreateServerFields) => {
    const normalized = normalizeServerInput(values.name ?? '', values.icon ?? '');
    void executeCreate(normalized.name, normalized.icon);
  };

  const validateName = (_: unknown, value: string) => {
    const error = validateServerInput(normalizeServerInput(value ?? '', '')).name;
    return error ? Promise.reject(new Error(error)) : Promise.resolve();
  };

  const validateIcon = (_: unknown, value: string) => {
    const error = validateServerInput(normalizeServerInput('Project', value ?? '')).icon;
    return error ? Promise.reject(new Error(error)) : Promise.resolve();
  };

  return (
    <Modal
      open
      title="Create Project"
      okText={serverError ? 'Retry' : 'Create Project'}
      cancelText="Cancel"
      confirmLoading={busy}
      okButtonProps={{ disabled: busy }}
      cancelButtonProps={{ disabled: busy }}
      closable={!busy}
      keyboard={!busy}
      mask={{ closable: !busy }}
      onOk={() => {
        if (createRecovery) void executeCreate();
        else form.submit();
      }}
      onCancel={close}
      afterOpenChange={(open) => {
        if (open) nameRef.current?.focus();
      }}
      afterClose={restoreFocus}
      focusable={{ focusTriggerAfterClose: false }}
      destroyOnHidden
      className="create-server-modal"
      width={440}
    >
      <Form<CreateServerFields>
        form={form}
        layout="vertical"
        initialValues={{
          name: createRecovery?.kind === 'intent' ? createRecovery.name : '',
          icon: createRecovery?.kind === 'intent' ? createRecovery.icon : '',
        }}
        onFinish={submit}
        onFinishFailed={({ errorFields }) => {
          const first = errorFields[0]?.name[0];
          if (first) form.getFieldInstance(first)?.focus?.();
        }}
        onValuesChange={() => {
          if (serverError && !createRecovery) setCreateError('');
        }}
        preserve={false}
      >
        <Form.Item
          label="Project name"
          htmlFor="create-server-name"
          name="name"
          rules={[{ validator: validateName }]}
          validateTrigger="onSubmit"
        >
          <Input id="create-server-name" ref={nameRef} autoComplete="off" disabled={fieldsLocked} />
        </Form.Item>
        <Form.Item
          label="Icon (optional)"
          htmlFor="create-server-icon"
          name="icon"
          rules={[{ validator: validateIcon }]}
          validateTrigger="onSubmit"
        >
          <Input id="create-server-icon" autoComplete="off" disabled={fieldsLocked} />
        </Form.Item>
        {serverError && <Alert type="error" showIcon message={serverError} role="alert" />}
      </Form>
    </Modal>
  );
}
